import React from 'react';
import { CheckCircle, XCircle, Loader, Clock, RotateCw } from 'lucide-react';
import { Button } from '../ui';
import { UI_TEXT } from '../../constants';

export const BatchTaskProgress = ({ tasks, onReset }) => {
  const { STATUS_TEXT } = UI_TEXT;
  
  const pending = tasks.filter(t => t.status === 'pending').length;
  const processing = tasks.filter(t => t.status === 'processing').length;
  const completed = tasks.filter(t => t.status === 'completed').length;
  const failed = tasks.filter(t => t.status === 'failed' || t.status === 'timeout').length;
  
  const allFinished = pending === 0 && processing === 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mt-3">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
        <h2 className="text-base font-semibold text-slate-800">批量任务进度</h2>
        {allFinished && (
          <Button
            size="sm"
            onClick={onReset}
            className="border-indigo-200 bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
          >
            <RotateCw className="mr-1.5 h-4 w-4" />
            开始新的批量充值
          </Button>
        )}
      </div>

      <div className="p-4">
        <div className="mb-4">
          <div className="flex justify-between text-sm text-slate-600 mb-2">
            <span>总进度</span>
            <span>{completed + failed}/{tasks.length}</span>
          </div>
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div className="flex h-full">
              <div
                className="bg-green-500 h-full transition-all duration-300"
                style={{ width: `${(completed / tasks.length) * 100}%` }}
                title={`已完成: ${completed}`}
              ></div>
              <div
                className="bg-indigo-500 h-full transition-all duration-300"
                style={{ width: `${(processing / tasks.length) * 100}%` }}
                title={`处理中: ${processing}`}
              ></div>
              <div
                className="bg-slate-300 h-full transition-all duration-300"
                style={{ width: `${(pending / tasks.length) * 100}%` }}
                title={`等待中: ${pending}`}
              ></div>
              <div
                className="bg-red-500 h-full transition-all duration-300"
                style={{ width: `${(failed / tasks.length) * 100}%` }}
                title={`失败: ${failed}`}
              ></div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Clock className="w-4 h-4 text-slate-500 mr-1" />
              <span className="text-xs font-medium text-slate-500">{STATUS_TEXT.WAITING}</span>
            </div>
            <div className="text-lg font-semibold text-slate-700">{pending}</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Loader className="w-4 h-4 text-indigo-500 mr-1" />
              <span className="text-xs font-medium text-indigo-500">{STATUS_TEXT.PROCESSING}</span>
            </div>
            <div className="text-lg font-semibold text-indigo-700">{processing}</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
              <span className="text-xs font-medium text-green-500">{STATUS_TEXT.COMPLETED}</span>
            </div>
            <div className="text-lg font-semibold text-green-700">{completed}</div>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <XCircle className="w-4 h-4 text-red-500 mr-1" />
              <span className="text-xs font-medium text-red-500">{STATUS_TEXT.FAILED}</span>
            </div>
            <div className="text-lg font-semibold text-red-700">{failed}</div>
          </div>
        </div>

        <div className="mt-2 max-h-32 overflow-y-auto bg-white rounded border border-gray-200 text-xs">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-2 py-1 text-left text-gray-500 uppercase">#</th>
                <th className="px-2 py-1 text-left text-gray-500 uppercase">邮箱</th>
                <th className="px-2 py-1 text-left text-gray-500 uppercase">兑换码</th>
                <th className="px-2 py-1 text-left text-gray-500 uppercase">状态</th>
                <th className="px-2 py-1 text-left text-gray-500 uppercase">重试</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tasks.map((task, index) => (
                <tr key={index} className={
                  task.status === 'completed' ? 'bg-green-50' :
                  task.status === 'failed' || task.status === 'timeout' ? 'bg-red-50' :
                  task.status === 'processing' ? 'bg-indigo-50' :
                  'bg-slate-50'
                }>
                  <td className="px-2 py-1">{index + 1}</td>
                  <td className="px-2 py-1 truncate max-w-[100px]" title={task.email || task.account}>
                    {task.email || task.account}
                  </td>
                  <td className="px-2 py-1 font-mono">{task.code}</td>
                  <td className="px-2 py-1">
                    {task.status === 'pending' && (
                      <div className="flex items-center">
                        <Clock className="h-3 w-3 text-slate-500 inline mr-1" />
                        <span className="text-slate-600">等待中</span>
                      </div>
                    )}
                    {task.status === 'processing' && (
                      <div className="flex items-center">
                        <Loader className="animate-spin h-3 w-3 text-indigo-600 inline mr-1" />
                        <span className="text-indigo-600">处理中</span>
                      </div>
                    )}
                    {task.status === 'completed' && (
                      <div className="flex items-center">
                        <CheckCircle className="h-3 w-3 text-green-600 inline mr-1" />
                        <span className="text-green-600">完成</span>
                      </div>
                    )}
                    {task.status === 'failed' && (
                      <div className="flex items-center">
                        <XCircle className="h-3 w-3 text-red-600 inline mr-1" />
                        <span className="text-red-600" title={task.error}>错误</span>
                      </div>
                    )}
                    {task.status === 'timeout' && (
                      <div className="flex items-center">
                        <Clock className="h-3 w-3 text-orange-600 inline mr-1" />
                        <span className="text-orange-600">超时</span>
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1 text-center">
                    {task.retryCount > 0 && (
                      <span className="text-xs text-gray-500">{task.retryCount}/3</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};